<script lang="ts" setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { LanguageContext } from '@nzyme/i18n/LanguageContext.js';
import type { Json } from '@nzyme/utils/toJson.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import type { ChatEvent } from '@sigma/chat-common/ChatEvent.js';
import { useLocale } from '@sigma/client/i18n/useLocale.js';
import { getFormInputValueSnippet } from '@sigma/common/forms/getFormInputValueSnippet.js';
import type { ChatListEndpointResult } from '@sigma/patient-api/chat/ChatListEndpoint.js';
import { ChatRoute } from '@sigma/patient-common/routes.js';
import ChatListItem from '@sigma/ui/chat/ChatListItem.vue';
import { formatRelativeDate } from '@sigma/utils/format/formatRelativeDate.js';
import { getFullName } from '@sigma/utils/getFullName.js';

import * as l from './PatientMainChatItem.loc.js';

const props = defineProps<{
  chat: ChatListEndpointResult;
}>();

const t = useTranslate();
const lang = useService(LanguageContext);
const locale = useLocale();

const hasStaff = computed(() => props.chat.staffId != null);
const staffFullName = computed(() => getFullName(props.chat.staffFirstName, props.chat.staffLastName));

const snippet = computed(() => describeEvent(props.chat.state.lastEvent));
const prefixedText = computed(() => {
  const { prefix, text } = snippet.value;
  if (!text) {
    return '';
  }
  return prefix ? `${prefix}: ${text}` : text;
});
const italic = computed(() => snippet.value.italic);

const unread = computed(() => {
  const event = props.chat.state.lastEvent;
  if (!event || props.chat.eventSeqRead == null) {
    return false;
  }
  return event.seq > props.chat.eventSeqRead;
});

const formattedTime = computed(() => {
  const event = props.chat.state.lastEvent;
  return event ? formatRelativeDate(event.timestamp, lang()) : '';
});

const chatRoute = computed(() => ChatRoute({ locale: locale.value, chatId: props.chat.id }));

interface EventDescription {
  prefix: string;
  text: string;
  italic: boolean;
}

const EMPTY: EventDescription = { prefix: '', text: '', italic: false };

function describeEvent(event: Json<ChatEvent> | undefined): EventDescription {
  if (!event) {
    return EMPTY;
  }
  switch (event.type) {
    case 'AGENT_DONE':
      return { prefix: t(l.healed), text: event.message, italic: false };
    case 'AGENT_MESSAGE':
      if (event.target === 'STAFF') {
        return EMPTY;
      }
      return { prefix: t(l.healed), text: event.message, italic: false };
    case 'AGENT_QUESTION':
      if (event.target === 'STAFF') {
        return EMPTY;
      }
      return { prefix: t(l.healed), text: event.input.question, italic: false };
    case 'AGENT_SUMMARY':
      return { prefix: t(l.healed), text: event.summary, italic: false };
    case 'CHAT_END':
      return { prefix: '', text: t(l.chatEnded), italic: true };
    case 'PATIENT_ANSWER':
      return {
        prefix: t(l.you),
        text: getFormInputValueSnippet({ answer: event.answer, lang: lang() }),
        italic: false,
      };
    case 'PATIENT_MESSAGE':
      if (event.content.trim() === '' && event.files && event.files.length > 0) {
        return { prefix: '', text: t(l.attachedFiles), italic: true };
      }
      return { prefix: t(l.you), text: event.content, italic: false };
    case 'PROFILE_DATA_REQUEST':
      return { prefix: '', text: t(l.profileDataRequested), italic: true };
    case 'PROFILE_DATA_SENT':
      return { prefix: '', text: t(l.profileDataSent), italic: true };
    case 'SIDE_EFFECTS':
      // Only document side effects surface a preview; observation-only markers show nothing.
      return event.documentId != null ? { prefix: '', text: t(l.documentParsed), italic: true } : EMPTY;
    case 'STAFF_ANSWER':
      return {
        prefix: staffPrefix(),
        text: getFormInputValueSnippet({ answer: event.answer, lang: lang() }),
        italic: false,
      };
    case 'STAFF_JOIN':
      return {
        prefix: '',
        text: t(l.doctorJoinNamed, { name: staffFullName.value }),
        italic: true,
      };
    case 'STAFF_MESSAGE':
      if (event.message.trim() === '' && event.files && event.files.length > 0) {
        return { prefix: '', text: t(l.attachedFiles), italic: true };
      }
      return { prefix: staffPrefix(), text: event.message, italic: false };
    case 'STAFF_PROMPT':
      return { prefix: staffPrefix(), text: event.message, italic: false };
    case 'SUBSCRIPTION_REQUEST':
      return { prefix: '', text: t(l.subscriptionRequested), italic: true };
    case 'SUBSCRIPTION_START':
      return { prefix: '', text: t(l.subscriptionStarted), italic: true };
    default:
      return EMPTY;
  }
}

function staffPrefix(): string {
  return staffFullName.value ? t(l.doctorPrefix, { name: staffFullName.value }) : '';
}
</script>

<template>
  <RouterLink
    :to="chatRoute"
    :class="[css.item, unread && css.unread]"
  >
    <ChatListItem
      :type="hasStaff ? 'person' : 'agent'"
      :first-name="chat.staffFirstName"
      :last-name="chat.staffLastName"
      :name-fallback="t(l.healed)"
      :snippet="prefixedText"
      :snippet-italic="italic"
      :time="formattedTime"
      :unread="unread"
    />
  </RouterLink>
</template>

<style lang="scss" module="css">
.item {
  display: block;
  padding: $spacing-14 $spacing-20;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.1s ease-out;

  &:hover {
    background: $color-doctor-50;
  }
}

.unread {
  background: $color-doctor-50;
  padding-left: 17px;
}
</style>
